// utils/common.js
// ══════════════════════════════════════════════════════════════
// COMMON UTILITIES — one home for small helpers used all over the app
// ══════════════════════════════════════════════════════════════
// Why this file exists:
// The code-duplication audit (audits/code-duplication-audit-2026-07-07.md)
// found the same tiny helpers copied into many files: money formatting,
// phone formatting, "time ago" text, status labels/colors, the list of
// canceled statuses, and the "is this a native app?" check.
//
// This module is the SINGLE place those helpers should live. It is PURE
// and dependency-light, so importing it is cheap and safe. It also
// RE-EXPORTS the helpers that already have a canonical home elsewhere
// (tier labels, LEU waterfall, default market) so callers have one import
// surface to reach for.
//
// This file is ADDITIVE. It does not change any existing behavior. Call
// sites should be migrated to it one scoped PR at a time (see the audit's
// "Migration order" section). Do not rewire protected surfaces
// (BookingFlow, pricing/checkout, LensIQ, scheduler, edge functions)
// without owner approval.
//
// Original IP — LoadDash Services LLC
// ══════════════════════════════════════════════════════════════

import { getCustomerTierLabel, LOAD_TIERS, getLEU } from "../constants";
import { jobLEU } from "./leuUtils";
import { DEFAULT_MARKET_ID } from "./marketConfig";

// ── Re-exports: helpers that already have a canonical home ──────
// Import these from here so there is one place to look, but the real
// definitions still live in constants.js / leuUtils.js / marketConfig.js.
export { getCustomerTierLabel, LOAD_TIERS, getLEU, jobLEU, DEFAULT_MARKET_ID };

// ── Money ───────────────────────────────────────────────────────

/**
 * Format a dollar amount like "$75.00".
 * Bad or missing values become "$0.00" so the screen never shows "$NaN".
 * @param {number} dollars
 * @returns {string}
 */
export function formatCurrency(dollars) {
  if (dollars == null || isNaN(dollars)) return "$0.00";
  return "$" + Number(dollars).toFixed(2);
}

/**
 * Format an amount that is stored in CENTS, like Stripe uses.
 * 7500 cents becomes "$75.00".
 * @param {number} cents
 * @returns {string}
 */
export function formatCents(cents) {
  if (cents == null || isNaN(cents)) return "$0.00";
  return formatCurrency(Number(cents) / 100);
}

// ── Phone ───────────────────────────────────────────────────────

/**
 * Keep only the number characters from a phone string.
 * "(801) 555-1234" becomes "8015551234".
 * @param {string} raw
 * @returns {string}
 */
export function phoneDigits(raw) {
  if (typeof raw !== "string") return "";
  return raw.replace(/\D/g, "");
}

/**
 * Make a US phone number look nice for the screen: "(801) 555-1234".
 * This is for SHOWING a number to a person. To SAVE a number, use
 * toE164US() from phoneCapture.js instead.
 * @param {string} raw
 * @returns {string} formatted number, or the digits typed so far
 */
export function formatUSPhone(raw) {
  const d = phoneDigits(raw).slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ── Time ────────────────────────────────────────────────────────

/**
 * Turn a time into short "how long ago" text: "5m", "3h", "2d", "just now".
 * @param {string|number|Date} when - an ISO string, ms number, or Date
 * @returns {string}
 */
export function timeAgo(when) {
  if (when == null) return "";
  const then = new Date(when).getTime();
  if (isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// ── Request statuses ────────────────────────────────────────────
// These lists were copied into many files with small differences,
// which is risky. Use these shared lists instead.

/** Statuses that mean the booking was canceled or turned down. */
export const CANCELED_STATUSES = Object.freeze([
  "canceled_by_customer",
  "canceled_by_admin",
  "declined",
]);

/** Statuses that mean a real booking is placed and taking up a slot. */
export const PLACED_STATUSES = Object.freeze([
  "available",
  "pending",
  "pending_priority",
  "claimed",
  "en_route",
  "in_progress",
  "complete",
]);

/** Payment statuses that count as actually paid. */
export const PAID_PAYMENT_STATUSES = Object.freeze(["succeeded", "verified"]);

/**
 * True when a request is NOT canceled/declined.
 * @param {{status?: string}} request
 * @returns {boolean}
 */
export function isActiveRequest(request) {
  return !CANCELED_STATUSES.includes(request?.status);
}

// ── Status display (label + color) ──────────────────────────────
// One place for the words and colors shown for each status. Keep
// admin-facing and Hauler-facing WORDING in the page if it must differ,
// but the color mapping can be shared.

/** Plain words for each status, safe for customer or admin screens. */
export const STATUS_LABEL = Object.freeze({
  checkout_pending: "Checkout pending",
  pending: "Pending review",
  pending_priority: "Pending (priority)",
  available: "Ready",
  claimed: "Claimed",
  en_route: "On the way",
  in_progress: "In progress",
  complete: "Complete",
  awaiting_priority_fallback: "Awaiting choice",
  canceled_by_customer: "Canceled",
  canceled_by_admin: "Canceled",
  declined: "Declined",
});

/**
 * Get the label for a status, or a neutral dash for unknown ones.
 * @param {string} status
 * @returns {string}
 */
export function statusLabel(status) {
  return STATUS_LABEL[status] || "—";
}

// ── Native app (Capacitor) ──────────────────────────────────────
// The same getCapacitor()/isNativePlatform() check was copied into
// lidarBridge.js and captureCapability.js. Use these instead.

/** @returns {object|null} the Capacitor global, or null on the web */
export function getCapacitor() {
  return typeof window !== "undefined" && window.Capacitor ? window.Capacitor : null;
}

/** @returns {boolean} true only inside the native (iOS/Android) app */
export function isNativePlatform() {
  const c = getCapacitor();
  return !!(c && typeof c.isNativePlatform === "function" && c.isNativePlatform());
}

/** @returns {string|null} "ios" | "android" | "web" | null */
export function getPlatform() {
  const c = getCapacitor();
  return c && typeof c.getPlatform === "function" ? c.getPlatform() : null;
}
